// /src/data/books/FundamentosBlockchain/data/cap2.js

const CAP2 = {
  id: 2,
  slug: "cap2-algoritmos-consenso",
  title: "Capítulo 2 — Otros tipos de algoritmos de consenso en blockchain",
  intro: [
    "En el **Capítulo 1** entendimos que blockchain no funciona gracias a una sola persona o servidor central, sino que depende de un conjunto de nodos que deben ponerse de acuerdo para validar la información.",
    "Ese acuerdo se llama **consenso**, y existen varios métodos para lograrlo. Ya conocimos los más famosos (Proof of Work y Proof of Stake), pero no son los únicos.",
    "En este capítulo veremos otros algoritmos de consenso que ofrecen **velocidad, eficiencia, escalabilidad o seguridad adicional**, especialmente en redes privadas, empresariales o de alto rendimiento."
  ],
  sections: [
    {
      heading: "¿Qué es un algoritmo de consenso?",
      text: [
        "Un algoritmo de consenso es el mecanismo mediante el cual diferentes participantes de una red blockchain **se ponen de acuerdo sobre qué información es válida**, sin necesidad de confiar unos en otros.",
        "Su objetivo principal es evitar que alguien pueda modificar datos, gastar monedas dos veces o introducir información falsa.",
        "Distintos proyectos usan distintos algoritmos, según sus necesidades: velocidad, seguridad, descentralización o eficiencia energética."
      ]
    },

    // 1. Proof of Authority
 {
  heading: "Proof of Authority (PoA)",
  text: [
    "Proof of Authority (PoA)** se basa en la identidad y reputación de validadores autorizados. Aquí no cualquiera puede validar bloques: solo personas, entidades o instituciones verificadas previamente.",
    "En lugar de demostrar poder computacional (PoW) o cantidad de monedas bloqueadas (PoS), los validadores muestran su **credibilidad y responsabilidad pública**.",
    "Es común en redes privadas, organizaciones o gobiernos, donde importa más la velocidad, el control y la confianza que la descentralización total."
  ],
  subsections: [
    {
      heading: "Ventajas:",
      list: [
        "Alta velocidad y eficiencia.",
        "Costos operativos bajos.",
        "No requiere equipos potentes ni gran consumo de energía."
      ]
    },
    {
      heading: "Desventajas:",
      list: [
        "Menor privacidad: los validadores deben identificarse.",
        "Riesgo de centralización del poder.",
        "Posible censura o manipulación si los validadores se ponen de acuerdo."
      ]
    },
    {
      heading: "Ideal para:",
      list: [
        "Redes privadas o empresariales.",
        "Entornos académicos o gubernamentales.",
        "Casos donde se necesita velocidad, trazabilidad y control de acceso."
      ]
    }
  ]
},

  {
  heading: "Proof of History (PoH)",
  text: [
    "Proof of History introduce un concepto clave: **el tiempo también puede formar parte del consenso**.",
    "Este algoritmo genera una secuencia criptográfica que demuestra que ciertos eventos ocurrieron en un momento exacto y en un orden específico, sin que todos los nodos tengan que esperar entre sí.",
    "Esto permite validar transacciones **con gran velocidad**, manteniendo seguridad y orden cronológico."
  ],
  subsections: [
    {
      heading: "Ventajas:",
      list: [
        "Velocidades extremadamente altas (decenas de miles de transacciones por segundo).",
        "Excelente escalabilidad sin sacrificar seguridad.",
        "Compatible con otros algoritmos como Proof of Stake (PoS)."
      ]
    },
    {
      heading: "Desventajas:",
      list: [
        "Implementación técnicamente compleja.",
        "Poca adopción fuera de redes especializadas.",
        "Requiere infraestructura más potente que otros algoritmos."
      ]
    },
    {
      heading: "Ideal para:",
      list: [
        "Pagos masivos y micropagos en tiempo real.",
        "Aplicaciones de gran volumen (juegos, redes sociales, marketplaces).",
        "Sistemas logísticos o financieros que requieren confirmación casi instantánea."
      ]
    }
  ]
},


    // 3. Hybrid PoW/PoS
   {
  heading: "Hybrid PoW/PoS",
  text: [
    "Hybrid PoW/PoS combina las fortalezas de Proof of Work (PoW) y Proof of Stake (PoS) para equilibrar **seguridad, sostenibilidad y resistencia a ataques**.",
    "La minería (PoW) ayuda a proteger la red y evitar ataques del 51%.",
    "El staking (PoS) reduce el consumo de energía y favorece procesos más eficientes.",
    "El resultado es un sistema donde la seguridad depende tanto de los recursos computacionales como del compromiso económico de los participantes."
  ],
  subsections: [
    {
      heading: "Ventajas:",
      list: [
        "Muy resistente a ataques y manipulaciones.",
        "Consumo energético moderado en comparación con PoW puro.",
        "Más flexible y adaptable que usar PoW o PoS por separado."
      ]
    },
    {
      heading: "Desventajas:",
      list: [
        "Difícil de implementar y mantener.",
        "Necesita reglas claras de gobernanza para equilibrar ambos mecanismos.",
        "Puede generar complejidad adicional para desarrolladores y validadores."
      ]
    },
    {
      heading: "Ideal para:",
      list: [
        "Redes que buscan combinar seguridad, sostenibilidad y estabilidad.",
        "Modelos de gobernanza descentralizada.",
        "Sistemas que necesitan protección contra ataques del 51% sin depender solo de minería."
      ]
    }
  ]
},

    {
  heading: "Delayed Proof of Work (dPoW)",
  text: [
    "Delayed Proof of Work (dPoW) es un modelo de consenso que permite que una blockchain más pequeña **se proteja utilizando la seguridad de otra blockchain más grande**.",
    "En lugar de depender solo de sus propios validadores, una red dPoW registra periódicamente sus datos en una blockchain más segura, como si estuviera haciendo copias de seguridad protegidas.",
    "Es como guardar documentos importantes en tu casa, pero hacerles una copia y depositarla también en una bóveda bancaria que tiene mucha más seguridad."
  ],
  subsections: [
    {
      heading: "Ventajas:",
      list: [
        "Aumenta significativamente la seguridad al respaldar los datos en otra blockchain más robusta.",
        "Útil para redes emergentes o con poco poder computacional.",
        "Permite heredar seguridad sin sacrificar la eficiencia de la red principal."
      ]
    },
    {
      heading: "Desventajas:",
      list: [
        "Depende de la estabilidad y seguridad de la blockchain externa.",
        "Incorpora costos adicionales (tarifas, infraestructura).",
        "Puede agregar complejidad técnica y operativa."
      ]
    },
    {
      heading: "Ideal para:",
      list: [
        "Blockchains nuevas que buscan mayor protección sin construir su propia infraestructura de seguridad.",
        "Proyectos pequeños que desean resistencia contra manipulaciones o ataques del 51%.",
        "Redes que necesitan seguridad reforzada sin sacrificar rendimiento ni flexibilidad."
      ]
    }
  ]
},

{
  heading: "Proof of Burn (PoB)",
  text: [
    "Proof of Burn (PoB) es un tipo de consenso en el que los participantes demuestran su compromiso con la red **“quemando” monedas**, es decir, enviándolas a una dirección de la que nadie puede recuperar los fondos.",
    "Al destruir voluntariamente parte de sus monedas, los validadores muestran que están dispuestos a asumir un costo real para participar en la seguridad de la red.",
    "La idea es similar a comprar una “participación” permanente: quemas hoy para ganar derecho a validar bloques y recibir recompensas en el futuro."
  ],
  subsections: [
    {
      heading: "Ventajas:",
      list: [
        "Reduce la oferta circulante, lo que en teoría puede beneficiar el valor de las monedas que quedan.",
        "Evita el uso intensivo de energía de modelos como Proof of Work.",
        "Introduce un costo económico real para atacar la red (hay que quemar muchas monedas)."
      ]
    },
    {
      heading: "Desventajas:",
      list: [
        "La quema es irreversible: si el proyecto fracasa, el valor destruido no se recupera.",
        "Puede ser difícil de entender para usuarios nuevos.",
        "No garantiza por sí mismo una amplia descentralización, depende de cómo se reparta la participación."
      ]
    },
    {
      heading: "Ideal para:",
      list: [
        "Redes que buscan un mecanismo alternativo al Proof of Work sin depender solo de staking.",
        "Sistemas que quieran introducir un compromiso económico fuerte y visible por parte de los validadores.",
        "Modelos experimentales donde la quema forme parte del diseño de incentivos del token."
      ]
    }
  ]
},



    // 5. Otros mecanismos de consenso
    {
      heading: "Otros algoritmos interesantes",
      text: [
        "Además de los grandes métodos, existen modelos menos conocidos pero útiles en escenarios específicos:"
      ],
      list: [
        "Proof of Burn (PoB): los validadores 'queman' monedas (las destruyen) para demostrar que están comprometidos con la seguridad de la red.",
        "Proof of Elapsed Time (PoET):usa un temporizador seguro para decidir qué nodo puede validar el próximo bloque (suele depender de hardware especializado).",
        "XRP Ledger Consensus: se basa en nodos confiables que se comunican entre sí para llegar a un consenso rápido, ideal para redes de pago."
      ]
    },

    // Conclusión
    {
      heading: "Conclusión del capítulo",
      text: [
        "No existe un único algoritmo perfecto. Cada mecanismo tiene un propósito: unos privilegian la velocidad, otros la seguridad, la eficiencia o la resistencia a censura.",
        "Lo importante es entender que **el consenso es el corazón de blockchain**, y su diseño determina si una red será abierta, rápida, privada, segura o incluso todo al mismo tiempo.",
        "En los próximos capítulos empezaremos a conectar estas ideas con aspectos prácticos: cómo se firman transacciones, cómo proteger tus claves y qué papel juegan los usuarios en la seguridad general de la red."
      ]
    }
  ]
};

export default CAP2;
